import Decimal from 'decimal.js';

import { DiagramDispositionsFormRecipient } from '@/modules/dispositiveProvisions/contexts/DispositiveDiagramSync.context';
import { RecipientKind } from '@/modules/dispositiveProvisions/DispositiveProvisionsForm/DispositiveProvisionsForm.types';
import {
  AfterDeath,
  ClientOrganizationKind,
  EntityInEstateStatus,
  EntityKind,
  TestamentaryEntityKind,
} from '@/types/schema';
import { UnreachableError } from '@/utils/errors';

import { EntityDiagram_NodeFragment, EntityDiagramTileKind } from '../types';

export interface MakeNodeFromEntityInput {
  entity: {
    id: string;
    entityKind: EntityKind;
    extendedDisplayKind: string;
    subtype: {
      id: string;
      displayName: string;
      inEstateStatus?: EntityInEstateStatus | null;
      policies?:
        | {
            id: string;
            deathBenefitAmount: Decimal;
          }[]
        | null;
    };
  };
  afterDeath: AfterDeath;
  ownershipPercentage?: Decimal;
  hasIncomingPouroverDisposition?: boolean;
  hasOutgoingPouroverDisposition?: boolean;
  entityDiagramTileKind: EntityDiagramTileKind;
}

export function makeNodeFromEntity({
  entity,
  afterDeath,
  ownershipPercentage,
  hasIncomingPouroverDisposition = false,
  hasOutgoingPouroverDisposition = false,
  entityDiagramTileKind,
}: MakeNodeFromEntityInput): EntityDiagram_NodeFragment {
  let estateStatus = EntityInEstateStatus.InEstate;

  if ('inEstateStatus' in entity.subtype) {
    estateStatus =
      entity.subtype.inEstateStatus ?? EntityInEstateStatus.InEstate;
  }

  let policies:
    | {
        id: string;
        deathBenefitAmount: Decimal;
      }[]
    | undefined = undefined;

  if ('policies' in entity.subtype) {
    policies = entity.subtype.policies?.map((policy) => ({
      id: policy.id,
      deathBenefitAmount: policy.deathBenefitAmount,
    }));
  }

  return {
    id: entity.id,
    afterDeath,
    entityDiagramID: null,
    inEstateStatus: estateStatus,
    nodeConfiguration: null,
    entityDiagramTileKind,
    node: {
      __typename: 'Entity',
      id: entity.id,
      entityKind: entity.entityKind,
      extendedDisplayKind: entity.extendedDisplayKind,
      hasIncomingPouroverDisposition,
      hasOutgoingPouroverDisposition,
      ...(ownershipPercentage !== undefined && { ownershipPercentage }),
      subtype: {
        id: entity.subtype.id,
        displayName: entity.subtype.displayName,
        ...(policies !== undefined && { policies }),
      },
    },
  };
}

export interface MakeNodeFromTestamentaryEntityInput {
  testamentaryEntity: {
    id: string;
    displayName: string;
    inEstateStatus?: EntityInEstateStatus | null;
    testamentaryEntityKind: TestamentaryEntityKind;
  };
  afterDeath: AfterDeath;
  entityDiagramTileKind: EntityDiagramTileKind;
  hasIncomingPouroverDisposition: boolean;
  hasOutgoingPouroverDisposition?: boolean;
}

export function makeNodeFromTestamentaryEntity({
  testamentaryEntity,
  afterDeath,
  entityDiagramTileKind,
  hasIncomingPouroverDisposition,
  hasOutgoingPouroverDisposition = false,
}: MakeNodeFromTestamentaryEntityInput): EntityDiagram_NodeFragment {
  return {
    id: testamentaryEntity.id,
    afterDeath,
    entityDiagramID: null,
    inEstateStatus:
      testamentaryEntity.inEstateStatus ?? EntityInEstateStatus.InEstate,
    nodeConfiguration: null,
    entityDiagramTileKind,
    node: {
      __typename: 'TestamentaryEntity',
      id: testamentaryEntity.id,
      testamentaryEntityKind: testamentaryEntity.testamentaryEntityKind,
      displayName: testamentaryEntity.displayName,
      hasIncomingPouroverDisposition,
      hasOutgoingPouroverDisposition,
    },
  };
}

export interface MakeNodeFromIndividualInput {
  individual: {
    id: string;
    displayName: string;
  };
  afterDeath: AfterDeath;
  estateStatus: EntityInEstateStatus;
  ownershipPercentage?: Decimal;
  entityDiagramTileKind: EntityDiagramTileKind | EntityDiagramTileKind[];
  hasOutgoingPouroverDisposition?: boolean;
}

export function makeNodeFromIndividual({
  individual,
  afterDeath,
  estateStatus,
  ownershipPercentage,
  entityDiagramTileKind,
  hasOutgoingPouroverDisposition = false,
}: MakeNodeFromIndividualInput): EntityDiagram_NodeFragment {
  return {
    id: individual.id,
    afterDeath,
    entityDiagramID: null,
    inEstateStatus: estateStatus,
    nodeConfiguration: null,
    entityDiagramTileKind,
    node: {
      __typename: 'ClientProfile',
      id: individual.id,
      displayName: individual.displayName,
      ownershipPercentage,
      hasOutgoingPouroverDisposition,
    },
  };
}

export interface MakeNodeFromOrganizationInput {
  organization: {
    id: string;
    name: string;
    clientOrganizationKind?: ClientOrganizationKind | null;
  };
  afterDeath: AfterDeath;
  entityDiagramTileKind: EntityDiagramTileKind;
}

export function makeNodeFromOrganization({
  organization,
  afterDeath,
  entityDiagramTileKind,
}: MakeNodeFromOrganizationInput): EntityDiagram_NodeFragment {
  return {
    id: organization.id,
    afterDeath,
    entityDiagramID: null,
    inEstateStatus: EntityInEstateStatus.OutOfEstate,
    nodeConfiguration: null,
    entityDiagramTileKind,
    node: {
      __typename: 'ClientOrganization',
      id: organization.id,
      kind: organization.clientOrganizationKind ?? ClientOrganizationKind.Other,
      name: organization.name,
    },
  };
}

interface MakeNodeFromRecipientInput {
  recipient: DiagramDispositionsFormRecipient;
  afterDeath: AfterDeath;
  survivingSpouse?: {
    id: string;
    displayName: string;
  };
  firstGrantorDeathId: string;
  hasIncomingPouroverDisposition: boolean;
}

// Makes a diagram node from the DiagramDispositionsFormRecipient type
export function makeNodeFromRecipient({
  recipient,
  afterDeath,
  survivingSpouse,
  firstGrantorDeathId,
  hasIncomingPouroverDisposition,
}: MakeNodeFromRecipientInput) {
  if (!recipient.recipientId || !recipient.recipientKind) {
    return null;
  }

  switch (recipient.recipientKind) {
    case RecipientKind.Entity:
      return makeNodeFromEntity({
        entity: {
          id: recipient.recipientId,
          entityKind: recipient.entityKind as EntityKind,
          extendedDisplayKind: afterDeath === AfterDeath.None ? 'Root' : '',
          subtype: {
            id: recipient.subtypeId ?? recipient.recipientId,
            displayName: recipient.displayName,
            inEstateStatus: recipient.inEstateStatus,
          },
        },
        afterDeath,
        hasIncomingPouroverDisposition,
        entityDiagramTileKind:
          afterDeath === AfterDeath.None
            ? EntityDiagramTileKind.Entity
            : EntityDiagramTileKind.DispositionBeneficiary,
      });
    case RecipientKind.TestamentaryEntity:
      return makeNodeFromTestamentaryEntity({
        testamentaryEntity: {
          id: recipient.recipientId,
          displayName: recipient.displayName,
          testamentaryEntityKind:
            recipient.testamentaryEntityKind ??
            TestamentaryEntityKind.TestamentaryTrust,
          inEstateStatus: recipient.inEstateStatus,
        },
        afterDeath,
        entityDiagramTileKind: EntityDiagramTileKind.DispositionBeneficiary,
        hasIncomingPouroverDisposition,
      });
    case RecipientKind.Individual:
      return makeNodeFromIndividual({
        individual: {
          id: recipient.recipientId,
          displayName: recipient.displayName,
        },
        afterDeath,
        estateStatus:
          recipient.recipientId === firstGrantorDeathId ||
          recipient.recipientId === survivingSpouse?.id
            ? EntityInEstateStatus.InEstate
            : EntityInEstateStatus.OutOfEstate,
        entityDiagramTileKind: EntityDiagramTileKind.DispositionBeneficiary,
      });
    case RecipientKind.Organization:
      return makeNodeFromOrganization({
        organization: {
          id: recipient.recipientId,
          name: recipient.displayName,
          clientOrganizationKind: recipient.isCharitableOrganization
            ? ClientOrganizationKind.CharitableOrganization
            : ClientOrganizationKind.Other,
        },
        afterDeath,
        entityDiagramTileKind: EntityDiagramTileKind.DispositionBeneficiary,
      });
    case RecipientKind.SurvivingSpouse:
      return makeNodeFromIndividual({
        individual: {
          id: survivingSpouse?.id ?? '',
          displayName: survivingSpouse?.displayName ?? '',
        },
        afterDeath,
        estateStatus: EntityInEstateStatus.InEstate,
        entityDiagramTileKind: EntityDiagramTileKind.DispositionBeneficiary,
      });
    default:
      throw new UnreachableError({
        case: recipient.recipientKind,
        message: `Unknown recipient kind ${recipient.recipientKind}`,
      });
  }
}
